import type { GameState } from '../state/gameState';
import { nextId } from '../state/gameState';
import type { GameEmitter } from '../events';
import type { Rng } from '../rng';
import type { MatchRule } from '../rules/matchRule';
import type { EnemyState, PowerUpKind } from '../types';
import type { PaceParams } from '../config/gameConfig';
import { ARENA, POWERUPS, spawnIntervalMs, enemySpeed, levelFor, stageRampSeconds } from '../config/gameConfig';
import { ENEMY_KINDS } from '../config/enemyCatalog';
import { v3 } from '../math/vec3';

// Spawns enemies on the difficulty-driven cadence and tags each with the
// current speed, so the pace creeps up over a session. Difficulty runs
// band-relative to the current curriculum stage and through the player's pace
// preset (relaxed/standard/fast).
export function updateSpawnSystem(
  state: GameState,
  dt: number,
  rng: Rng,
  rule: MatchRule,
  pace: PaceParams,
  emit: GameEmitter,
): void {
  const level = levelFor(state.elapsed);
  if (level !== state.level) {
    state.level = level;
    emit.emit('levelChanged', { level });
  }
  state.spawnCountdown -= dt;
  if (state.spawnCountdown > 0) return;

  const rampSec = stageRampSeconds(state.elapsed, state.stageFromLevel);
  state.spawnCountdown += spawnIntervalMs(rampSec, pace) / 1000;

  // Expression rules produce value+display together; plain rules get a digit.
  const generated = rule.generate?.(rng) ?? null;
  const value = generated ? generated.value : rng.int(0, 10);
  const enemy: EnemyState = {
    id: nextId(state),
    value,
    display: generated ? generated.display : rule.displayFor(value),
    kind: rng.pick(ENEMY_KINDS),
    position: v3(rng.range(-ARENA.spawnXRange, ARENA.spawnXRange), ARENA.spawnY, ARENA.spawnZ),
    speed: enemySpeed(rampSec, pace),
    spin: rng.range(0.6, 0.9), // gentle Y spin, matching the legacy look
    spawnedAt: state.elapsed,
  };
  state.enemies.push(enemy);
  emit.emit('enemySpawned', { enemy });

  // Occasionally a golden star drifts in alongside the wave.
  if (rng.chance(POWERUPS.chance)) {
    const kinds: readonly PowerUpKind[] = ['timeFreeze', 'multiShot', 'hint'];
    const powerup = {
      id: nextId(state),
      kind: rng.pick(kinds),
      position: v3(rng.range(-ARENA.spawnXRange, ARENA.spawnXRange), ARENA.spawnY, ARENA.spawnZ),
      speed: POWERUPS.driftSpeed,
      spawnedAt: state.elapsed,
    };
    state.powerups.push(powerup);
    emit.emit('powerUpSpawned', { powerup });
  }
}
